import { css } from '@emotion/react';
import { responsive, useTheme } from '@wci-tech/core-ui';
import { useMemo } from 'react';

export const useStyles = () => {
  const MUTED_TEXT_COLOR = '#f5f6fa';
  const { theme } = useTheme();

  return useMemo(
    () => ({
      cardContainer: css`
        padding-top: 64px;
        padding-bottom: 64px;
        width: 100%;
        border: none;
        box-shadow: none;
        background: transparent;
      `,

      titleChipContainer: css`
        --chip-bg-color: #e8fafa15;

        ${responsive.create({
          padding: {
            default: `${theme.spacing.add(4, 2)} ${theme.spacing.get(8)}`,
            md: `${theme.spacing.get(8)} ${theme.spacing.get(10)}`,
            lg: `${theme.spacing.get(10)} ${theme.spacing.get(12)}`,
          },
          marginBottom: {
            default: theme.spacing.get(16),
            md: theme.spacing.get(24),
            lg: theme.spacing.get(24),
          },
        })}

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          padding: 8px 10px;
          margin-bottom: 20px;
        }
      `,

      titleChipLabel: css`
        font-weight: 600;
        font-size: 14px;
        color: ${MUTED_TEXT_COLOR};
        ${responsive.create({
          fontSize: {
            default: `${theme.fonts.size.xxs}`,
            lg: `${theme.fonts.size.sm}`,
          },
        })}
      `,

      imageContainer: css`
        width: 100%;
        display: flex;
        justify-content: center;
        overflow: hidden;
      `,

      cardOneImageContainer: css`
        width: 100%;
        display: flex;
        justify-content: center;
        overflow: hidden;

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          > * {
            width: 780px;
            max-width: 780px;
          }
        }
      `,

      descriptionChipContainer: css`
        --chip-bg-color: ${theme.colors.brandColors.secondary?.default};

        ${responsive.create({
          padding: {
            default: `${theme.spacing.add(4, 2)} ${theme.spacing.get(8)}`,
            md: `${theme.spacing.get(8)} ${theme.spacing.get(10)}`,
            lg: `${theme.spacing.get(10)} ${theme.spacing.get(20)}`,
          },
        })}

        margin-bottom: ${theme.spacing.get(16)};

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          padding: 8px 14px;
          margin-bottom: 10px;
        }
      `,

      descriptionChipLabel: css`
        font-weight: ${theme.fonts.weight.medium};
        color: ${theme.colors.commonColors.black};

        ${responsive.create({
          fontSize: {
            default: `${theme.fonts.size.xxs}`,
            md: `${theme.fonts.size.sm}`,
            xl: `${theme.fonts.size.md}`,
          },
        })}

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          font-size: 12px;
        }
      `,

      cardTitle: css`
        font-weight: ${theme.fonts.weight.medium};
        color: ${MUTED_TEXT_COLOR};
        text-align: center;

        ${responsive.create({
          fontSize: {
            default: `${theme.fonts.size.lg}`,
            md: `${theme.fonts.size.xxl}`,
            xl: `${theme.fonts.size['3xl']}`,
          },
        })}

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          font-size: 28px;
          line-height: 1.2;
        }
      `,

      outcomeContainer: css`
        & > svg,
        & > img {
          display: block;
          min-width: 0;
          min-height: 0;
        }
      `,

      bookDemoButton: css`
        --btn-bg-color: white;
        --btn-text-color: #333333;
        --btn-bg-hover-color: black;
        --btn-text-hover-color: ${MUTED_TEXT_COLOR};

        ${responsive.create({
          width: {
            default: '100%',
            md: 'auto',
          },
        })}
      `,

      cardDescription: css`
        text-align: center;
        color: ${MUTED_TEXT_COLOR};

        ${responsive.create({
          fontSize: {
            default: `${theme.fonts.size.xs}`,
            md: `${theme.fonts.size.sm}`,
            xl: `${theme.fonts.size.xl}`,
          },
          maxWidth: {
            default: 250,
            md: 480,
            xl: 650,
          },
        })}

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          font-size: 20px;
          line-height: 1.3;
        }
      `,

      iconContentLeftIcon: css`
        ${responsive.create({
          marginRight: {
            default: `${theme.spacing.get(4)}`,
            md: `${theme.spacing.get(4)}`,
            lg: `${theme.spacing.get(8)}`,
          },
          marginTop: {
            default: `${theme.spacing.get(0)}`,
            md: `${theme.spacing.get(4)}`,
            lg: `${theme.spacing.get(4)}`,
          },
        })}
      `,

      cardOneDescription: css`
        max-width: 680px;

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          max-width: 600px;
        }
      `,

      cardTwoDescription: css`
        ${responsive.create({
          maxWidth: {
            default: 245,
            md: 325,
            xl: 460,
          },
        })}
      `,

      footerText: css`
        text-align: center;
        margin: 0 auto;
        font-weight: ${theme.fonts.weight.medium};
        color: ${MUTED_TEXT_COLOR};
        font-style: italic;

        ${responsive.create({
          maxWidth: {
            default: 300,
            md: 600,
            xl: 745,
          },
          marginTop: {
            default: `${theme.spacing.get(16)}`,
            md: `${theme.spacing.get(24)}`,
            xl: `${theme.spacing.get(32)}`,
          },
          fontSize: {
            default: `${theme.fonts.size.xxs}`,
            md: `${theme.fonts.size.lg}`,
            xl: `${theme.fonts.size.xxl}`,
          },
        })}

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          margin-top: 16px;
          font-size: 24px;
          line-height: 1.3;
        }
      `,

      cardArrowContainer: css`
        ${responsive.create({
          marginBottom: {
            default: `${theme.spacing.get(12)}`,
            md: `${theme.spacing.get(44)}`,
            xl: `${theme.spacing.get(24)}`,
          },
        })}

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          margin-bottom: 12px;
        }
      `,

      firstCardfooterText: css`
        text-align: center;
        margin: 0 auto;
        font-weight: ${theme.fonts.weight.medium};
        color: ${MUTED_TEXT_COLOR};
        font-style: italic;

        ${responsive.create({
          marginTop: {
            default: `${theme.spacing.get(24)}`,
            md: `${theme.spacing.get(40)}`,
            xl: `${theme.spacing.get(24)}`,
          },
          fontSize: {
            default: `${theme.fonts.size.sm}`,
            md: `${theme.fonts.size.lg}`,
            xl: `${theme.fonts.size.xxl}`,
          },
          maxWidth: {
            default: 300,
            md: 600,
            xl: 745,
          },
        })}

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          margin-top: 12px;
          font-size: 24px;
          line-height: 1.3;
        }
      `,

      contentBox: css`
        margin-top: 24px;
        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          margin-top: 20px;
        }
      `,

      bodyText: css`
        text-align: center;
        margin-top: ${theme.spacing.get(20)};
        margin-bottom: ${theme.spacing.get(20)};
      `,

      cardThreebodyText: css`
        text-align: center;
        color: ${MUTED_TEXT_COLOR};
        margin-top: ${theme.spacing.get(20)};
        margin-bottom: ${theme.spacing.get(20)};

        ${responsive.create({
          display: {
            default: 'none', // Hidden on Mobile
            md: 'block', // Visible on Tablet
            lg: 'block', // Visible on Desktop
          },
          fontSize: {
            default: `${theme.fonts.size.xxs}`,
            md: `${theme.fonts.size.xs}`,
            xl: `${theme.fonts.size.md}`,
          },
          maxWidth: {
            default: 300,
            md: 600,
            xl: 745,
          },
        })}

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          margin-top: 16px;
          margin-bottom: 16px;
          font-size: 16px;
          line-height: 1.3;
        }
      `,

      wrapper: css`
        width: ${theme.spacing.get('100%')};
      `,

      buttonContainer: css`
        ${responsive.create({
          marginTop: {
            default: `${theme.spacing.get(30)}`,
            md: `${theme.spacing.get(16)}`,
            xl: `${theme.spacing.get(24)}`,
          },
          marginBottom: {
            default: `${theme.spacing.get(30)}`,
            md: `${theme.spacing.get(40)}`,
            xl: `${theme.spacing.get(20)}`,
          },
          width: {
            default: '100%',
            md: 'auto',
          },
        })}

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          margin-top: 20px;
          margin-bottom: 10px;
        }
      `,

      scrollTrack: css`
        ${responsive.create({
          paddingTop: {
            default: `${theme.spacing.get(16)}`,
            md: `${theme.spacing.get(40)}`,
            xl: `${theme.spacing.get(100)}`,
          },
          paddingBottom: {
            default: `${theme.spacing.get(16)}`,
            md: `${theme.spacing.get(40)}`,
            xl: `${theme.spacing.get(44)}`,
          },
          paddingLeft: {
            default: `${theme.spacing.get(16)}`,
            md: `${theme.spacing.get(24)}`,
            xl: `${theme.spacing.get(64)}`,
          },
          paddingRight: {
            default: `${theme.spacing.get(16)}`,
            md: `${theme.spacing.get(24)}`,
            xl: `${theme.spacing.get(64)}`,
          },
        })}

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          padding-top: 100px;
          padding-bottom: 16px;
        }
      `,

      infoBoxContainer: css`
        margin: 0 auto;

        ${responsive.create({
          maxWidth: {
            default: '100%',
            md: '600px',
            xl: '745px',
          },
        })}

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          width: 745px;
          max-width: 745px;
        }
      `,

      whyDifferentColumn: css`
        ${responsive.create({
          display: {
            default: 'none',
            md: 'block',
          },
        })}
      `,

      infoBox: css`
        background-color: rgba(15, 23, 42, 0.5);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        padding: 20px;
        height: 100%;
        text-align: left;
        backdrop-filter: blur(8px);

        ${responsive.create({
          padding: {
            default: `${theme.spacing.get(12)}`,
            md: `${theme.spacing.add(12, 2)}`,
            xl: `${theme.spacing.get(20)}`,
          },
        })}

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          padding: 14px;
        }
      `,

      boxTitle: css`
        color: ${MUTED_TEXT_COLOR};
        font-weight: 600;

        ${responsive.create({
          fontSize: {
            default: `${theme.fonts.size.xs}`,
            md: `${theme.fonts.size.sm}`,
            lg: `${theme.fonts.size.sm}`,
            xl: `${theme.fonts.size.xl}`,
          },
          marginBottom: {
            default: `${theme.spacing.get(10)}`,
            md: `${theme.spacing.get(12)}`,
            xl: `${theme.spacing.get(16)}`,
          },
        })}

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          font-size: 20px;
          margin-bottom: 8px;
        }
      `,

      boxTitleOutcome: css`
        color: ${theme.colors.brandColors.secondary?.default};
        font-weight: 600;

        ${responsive.create({
          fontSize: {
            default: `${theme.fonts.size.xs}`,
            md: `${theme.fonts.size.sm}`,
            xl: `${theme.fonts.size.xl}`,
          },
          marginBottom: {
            default: `${theme.spacing.get(10)}`,
            md: `${theme.spacing.get(12)}`,
            xl: `${theme.spacing.get(16)}`,
          },
        })}

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          font-size: 20px;
          margin-bottom: 8px;
        }
      `,

      iconListWrapper: css`
        display: flex;
        flex-direction: column;
      `,

      outcomeIconListWrapper: css`
        display: grid;

        ${responsive.create({
          gridTemplateColumns: {
            default: 'repeat(2, 1fr)',
            md: '1fr',
            lg: '1fr',
          },
        })}
      `,

      problemsColumn: css`
        ${responsive.create({
          display: {
            default: 'none',
            md: 'block',
          },
        })}
      `,

      cardChipContainer: css`
        ${responsive.create({
          marginTop: {
            default: `${theme.spacing.get(40)}`,
            md: `${theme.spacing.get(60)}`,
            xl: `${theme.spacing.get(20)}`,
          },
        })}

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          margin-top: 8px;
        }
      `,

      fullWidthMobile: css`
        ${responsive.create({
          width: {
            default: theme.spacing.get(188),
            md: 'auto',
          },
        })}
      `,

      listText: css`
        color: #b4bcf5;

        ${responsive.create({
          fontSize: {
            default: `${theme.fonts.size.xxs}`,
            md: `${theme.fonts.size.xxs}`,
            lg: `${theme.fonts.size.xxs}`,
            xl: `${theme.fonts.size.sm}`,
          },
        })}

        @media (min-width: 1280px) and (max-width: 1600px) and (max-height: 850px) {
          font-size: 14px;
          line-height: 1.25;
        }

        .desktop-text {
          ${responsive.create({
            display: {
              default: 'none',
              md: 'inline',
            },
          })}
        }

        .mobile-text {
          ${responsive.create({
            display: {
              default: 'inline',
              md: 'none',
            },
          })}
        }
      `,
    }),
    [theme],
  );
};
